import "./index.css";
import { IconButton, Typography } from "@/ui-kit";
import type { MouseEvent } from "react";
import { useCallback, useMemo } from "react";
import { Trash2Icon, XIcon } from "lucide-react";
import type { INotification } from "@/types/notifications";
import { NotificationsRepository } from "@/api/notifications/repository";
import { NotificationsService } from "@/api/notifications/service";
import { useNotificationsStore } from "@/store/notifications/slice";
import { useNavigate } from "react-router";
import { formatNotificationDate } from "../../../Notifications/utils";
import { clsx } from "clsx";
import { useBreakpoints } from "@/hooks/useBreakpoints";

type IProps = {
  item: INotification;
};

export const Notification = ({ item }: IProps) => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoints();

  const repository = useMemo(() => {
    return new NotificationsRepository(new NotificationsService(), {
      abort: true,
    });
  }, []);

  const handleRead = useCallback(() => {
    if (!item.isRead) {
      useNotificationsStore.getState().readNotification(item);
      repository.markAsRead({
        id: item.id,
      });
    }
    useNotificationsStore.getState().setOpen(false);
    const pathname = item.link.split(".com/")[1];
    navigate(`/${pathname}`);
  }, [item, navigate, repository]);

  const handleArchive = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();

      useNotificationsStore.getState().archiveNotification(item);
      repository.delete({
        id: item.id,
      });
    },
    [repository, item],
  );

  return (
    <div className="notification" onClick={handleRead}>
      <div
        className={clsx(
          "notification-source__marker",
          item.isRead && "notification-source__marker--read",
        )}
      />
      <div className="notification-details">
        <header className="notification-details__header">
          {item.title && (
            <Typography
              as="p"
              className="notification-title"
              variant={isMobile ? "headline" : "body"}
              weight="bold"
              text={item.title}
            />
          )}
          <div className="notification-actions">
            <IconButton
              size="sm"
              color="layer1"
              variant={isMobile ? "ghost" : "filled"}
              onClick={handleArchive}
            >
              {isMobile ? <Trash2Icon /> : <XIcon />}
            </IconButton>
          </div>
        </header>
        {item.message && (
          <Typography
            as="p"
            className="notification-text"
            variant={isMobile ? "headline" : "body"}
            weight="regular"
            text={item.message}
          />
        )}
        <div className="notification-time">
          <Typography
            variant={isMobile ? "headline" : "body"}
            text={formatNotificationDate(item.createdOn)}
          />
        </div>
      </div>
    </div>
  );
};
